import { useState } from "react";
import { Info } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";

/**
 * "Auto-vote" section. Phase 3 — a 4-tier radio (off / cast / submit /
 * submit-override) replacing the legacy on/off Switch. The terse `description`
 * line surfaces the headline behavior; tapping a row's info icon expands the
 * full free-time + conflict semantics inline. (Was a hover-only Tooltip, which
 * is unreachable on touch — a consequential setting must be readable on a phone.
 * Inline expansion also avoids nesting a Radix Popover inside this section's own
 * settings popover.)
 */

const TIERS = [
  {
    value: "off",
    label: "Off (visual hints only)",
    description: "Highlights helpful slots — but never votes for you.",
    longExplanation:
      "You'll see icons on poll slots — a star ⭐ on slots that fit your free time, and a warning ⚠ on slots that clash with your calendar — but you'll click every vote yourself. Pick this if you'd rather vote on everything by hand.",
  },
  {
    value: "cast",
    label: "Auto-cast (you submit)",
    description: "Pre-fills your votes. You still click Submit.",
    longExplanation:
      "On every open poll, we'll pre-fill your votes based on your free-time blocks, and skip any slot that clashes with your calendar. Nothing is final until you click Submit Votes — we just save you the typing.",
  },
  {
    value: "submit",
    label: "Auto-submit (keeps your edits)",
    description:
      "Votes for you automatically. Anything you change by hand stays put.",
    longExplanation:
      "We'll submit your votes for you — picking the slots that match your free time and clearing the ones that clash with your calendar. If you change a vote yourself, we leave it alone forever, even when your schedule changes later. Recommended for most people.",
  },
  {
    value: "submit-override",
    label: "Auto-submit with override",
    description:
      "Votes for you — and updates your votes when your schedule changes.",
    longExplanation:
      "Same as Auto-submit, plus: when you edit a free-time block, or a new event lands on your calendar, we'll update any vote that no longer matches — even ones you placed by hand. You'll get a notification each time so you can review or restore. Pick this if you want your votes to always reflect your latest schedule.",
  },
];

export function AutoVoteSection({
  autoVoteMode = "off",
  onAutoVoteModeChange,
  saving = false,
}) {
  const [expandedTier, setExpandedTier] = useState(null);

  return (
    <section className="space-y-2">
      <h3 className="font-semibold">Auto-vote</h3>
      <RadioGroup
        value={autoVoteMode}
        onValueChange={onAutoVoteModeChange}
        disabled={saving}
        className="space-y-2"
      >
        {TIERS.map((tier) => {
          const expanded = expandedTier === tier.value;
          return (
            <div
              key={tier.value}
              className="rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40"
            >
              <div className="flex items-start gap-2">
                <label
                  htmlFor={`auto-vote-${tier.value}`}
                  className="flex flex-1 cursor-pointer items-start gap-3"
                >
                  <RadioGroupItem
                    id={`auto-vote-${tier.value}`}
                    value={tier.value}
                    className="mt-1"
                    aria-label={tier.label}
                  />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {tier.label}
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {tier.description}
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  aria-label={`More about ${tier.label}`}
                  aria-expanded={expanded}
                  onClick={() =>
                    setExpandedTier(expanded ? null : tier.value)
                  }
                  className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                >
                  <Info className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              {expanded && (
                <p className="mt-2 border-t border-slate-200 pt-2 text-xs leading-relaxed text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  {tier.longExplanation}
                </p>
              )}
            </div>
          );
        })}
      </RadioGroup>
    </section>
  );
}
